import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { apiPostForm } from "@/lib/api";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";

const MAX_LEN = 3000;

export default function FreeWritePage() {
  const navigate = useNavigate();
  const { member, requireLogin } = useAuth();
  const { toast } = useToast();

  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<(File | null)[]>([null, null, null]);
  const [submitting, setSubmitting] = useState(false);

  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const fileRefs = [ref1, ref2, ref3];

  useEffect(() => {
    if (!member) requireLogin();
  }, [member, requireLogin]);

  if (!member) {
    return (
      <>
        <PageBanner title="자유글 쓰기" breadcrumb={[{ label: "자유글", to: "/free" }, { label: "글쓰기" }]} />
        <div className="mx-auto max-w-2xl px-4 md:px-6 py-16 text-center text-foreground-500">
          로그인 후 글을 작성할 수 있습니다.
        </div>
      </>
    );
  }

  const setFile = (index: number, file: File | null) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  const submit = async () => {
    const s = subject.trim();
    const c = content.trim();
    if (!c) {
      toast("내용을 입력해 주세요.", "info");
      return;
    }
    if (c.length > MAX_LEN) {
      toast(`내용은 ${MAX_LEN}자 이내로 입력해 주세요.`, "info");
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("act", "write");
      form.append("subject", s);
      form.append("content", c);
      files.forEach((f, i) => {
        if (f) form.append(`img${i + 1}`, f);
      });
      const res = await apiPostForm("free_write.php", form);
      toast(res.msg || "글이 등록되었습니다.", "success");
      navigate("/free");
    } catch (e) {
      toast(e instanceof Error ? e.message : "등록에 실패했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageBanner
        title="자유글 쓰기"
        description="회원들과 나누고 싶은 이야기를 자유롭게 남겨보세요."
        breadcrumb={[{ label: "자유글", to: "/free" }, { label: "글쓰기" }]}
      />

      <section className="mx-auto max-w-2xl px-4 md:px-6 py-10 md:py-12">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-7 space-y-5">
          <div>
            <label htmlFor="subject" className="mb-2 block text-sm font-semibold text-foreground-700">
              제목 (선택)
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={100}
              placeholder="제목을 입력하세요 (선택)"
              className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div>
            <label htmlFor="content" className="mb-2 block text-sm font-semibold text-foreground-700">
              내용 (필수)
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={MAX_LEN}
              rows={10}
              placeholder="내용을 입력하세요"
              className="w-full resize-y rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <p className="mt-1.5 text-right text-xs text-foreground-400">
              {content.length} / {MAX_LEN}
            </p>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-foreground-700">사진 첨부 (최대 3장)</p>
            <div className="flex flex-wrap gap-3">
              {[0, 1, 2].map((i) => {
                const file = files[i];
                return (
                  <div key={i} className="relative">
                    <button
                      type="button"
                      onClick={() => fileRefs[i].current?.click()}
                      className="h-24 w-24 overflow-hidden rounded-lg border border-dashed border-background-300 bg-background-50 text-foreground-400 hover:border-primary-400 hover:text-primary-600"
                    >
                      {file ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`첨부 이미지 ${i + 1}`}
                          className="h-full w-full object-cover object-top"
                        />
                      ) : (
                        <span className="flex h-full w-full flex-col items-center justify-center gap-1">
                          <i className="ri-image-add-line text-2xl" aria-hidden="true"></i>
                          <span className="text-xs">사진 {i + 1}</span>
                        </span>
                      )}
                    </button>
                    {file && (
                      <button
                        type="button"
                        onClick={() => setFile(i, null)}
                        aria-label="사진 삭제"
                        className="absolute -right-2 -top-2 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-900 text-background-50"
                      >
                        <i className="ri-close-line text-sm" aria-hidden="true"></i>
                      </button>
                    )}
                    <input
                      ref={fileRefs[i]}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        if (f && f.type.startsWith("image/")) setFile(i, f);
                        e.target.value = "";
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="flex-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
            >
              {submitting ? "등록 중..." : "등록하기"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/free")}
              className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
          </div>
        </div>
      </section>
    </>
  );
}